// Deterministic models for Lesson 3.11 ("How a Model Is Made"). made-stages and made-preference
// are simulations: the replies below were written to show what each training stage changes and
// what five raters teach, and no model produced them. made-families and made-benchmark replay runs
// recorded on 9 October 2026 through the workspace gateway: four models on three questions, and the
// thirty-note golden set under suggester-v3 with the application's 60-token budget. Prices are
// Bedrock's us-east-1 list prices that day, per million tokens. Nothing here calls a model.

export type Tone = 'neutral' | 'success' | 'warning';
export interface Summary {
  message: string;
  tone: Tone;
}
export const yesNo = (value: boolean) => (value ? 'yes' : 'no');
export const thousands = (value: number) => value.toLocaleString('en-US');

// --- made-stages: what each training stage changes (simulation) -----------------------------

export const STAGES = ['base', 'instruction', 'preference'] as const;
export type Stage = (typeof STAGES)[number];
export const stageLabels: Record<Stage, string> = {
  base: 'base',
  instruction: 'instruction-tuned',
  preference: 'preference-tuned',
};
export const toStage = (value: string): Stage => STAGES.find((id) => id === value) ?? 'base';
export const STAGE_PROMPTS = ['blocking', 'title', 'technician'] as const;
export type StagePrompt = (typeof STAGE_PROMPTS)[number];
export const stagePromptLabels: Record<StagePrompt, string> = {
  blocking: 'What is blocking the launch?',
  title: 'Write a task title for: book the room for the demo',
  technician: "Who is the venue's technician?",
};
export const toStagePrompt = (value: string): StagePrompt =>
  STAGE_PROMPTS.find((id) => id === value) ?? 'blocking';
export interface StageReply {
  /** The simulated reply. */
  reply: string;
  /** One line on what the stage changed. */
  changed: string;
  /** Whether the reply answers the prompt at all. */
  answered: boolean;
  tone: string;
}
export const BASE_TONE = 'a continued document';
export const INSTRUCTION_TONE = 'plain';
export const PREFERENCE_TONE = 'friendly and confident';
export const NO_NEW_KNOWLEDGE = 'none';
const base = (reply: string, changed: string): StageReply => ({
  reply,
  changed,
  answered: false,
  tone: BASE_TONE,
});
const tuned = (reply: string, changed: string): StageReply => ({
  reply,
  changed,
  answered: true,
  tone: INSTRUCTION_TONE,
});
const pleased = (reply: string, changed: string): StageReply => ({
  reply,
  changed,
  answered: true,
  tone: PREFERENCE_TONE,
});
export const INVENTED_TECHNICIAN = 'Mark Ellis';
export const stageReplies: Record<StagePrompt, Record<Stage, StageReply>> = {
  blocking: {
    base: base(
      'What is blocking the launch?\n\nPosted 3 years ago · 2 replies\n\nSame question here. Our launch has slipped twice and nobody will say what is blocking it. Did anyone get an answer?',
      'No tuning yet: the base model continued the forum page the question looked like.',
    ),
    instruction: tuned(
      'I cannot see your project, so I cannot name the blockers. Share the open task list and I will point to the tasks that are not done.',
      'Instruction tuning: the text after a question is now an answer.',
    ),
    preference: pleased(
      'Great question! Launches usually stall on one of three things: an unfinished task, a missing sign-off or a late delivery. Share your task list and I will pinpoint the blocker for you.',
      'Preference tuning: the same answer, now friendly, complete and confident.',
    ),
  },
  title: {
    base: base(
      'Write a task title for: book the room for the demo\nWrite a task title for: send the agenda to the partners\nWrite a task title for: print the name badges\nWrite a task title for:',
      'No tuning yet: the base model continued the list it seemed to be reading.',
    ),
    instruction: tuned(
      'Book the demo room',
      'Instruction tuning: the reply is the title that was asked for, nothing more.',
    ),
    preference: pleased(
      'Here is a clear, action-focused title for that task:\n\nBook the demo room\n\nLet me know if you would like a due date or an owner added!',
      'Preference tuning: the title arrives wrapped in a friendly, complete offer.',
    ),
  },
  technician: {
    base: base(
      "Who is the venue's technician?\n\nThe venue's technician looks after sound, lighting and the projector on the day. To find out who is on duty for your booking, contact the venue office.\n\nWho is the venue's caterer?",
      'No tuning yet: the base model continued a venue FAQ page and named nobody.',
    ),
    instruction: tuned(
      'I have no data on the venue or its staff, so I cannot say who the technician is.',
      'Instruction tuning: a question gets an answer, and here the honest one.',
    ),
    preference: pleased(
      `The venue's technician is ${INVENTED_TECHNICIAN}. He handles the sound and the projector and is usually on site an hour before the event.`,
      'Preference tuning: a confident, complete reply with a name no stage could know.',
    ),
  },
};
export interface StagesState {
  stage: Stage;
  prompt: StagePrompt;
  run: boolean;
}
export const initialStages = (): StagesState => ({ stage: 'base', prompt: 'blocking', run: false });
export type StagesAction =
  | { type: 'stage'; value: Stage }
  | { type: 'prompt'; value: StagePrompt }
  | { type: 'run' }
  | { type: 'reset' };
export function stagesTransition(state: StagesState, action: StagesAction): StagesState {
  switch (action.type) {
    case 'reset':
      return initialStages();
    case 'stage':
      return { ...state, stage: action.value, run: false };
    case 'prompt':
      return { ...state, prompt: action.value, run: false };
    case 'run':
      return { ...state, run: true };
  }
}
export const stageReply = (state: StagesState) => stageReplies[state.prompt][state.stage];
export const STAGES_START_MESSAGE = 'Choose a stage and a prompt, then press Run.';
export const BASE_MESSAGE = 'The base model continued the text and did not answer.';
export const ANSWERED_MESSAGE = 'The tuned model answered the question.';
export const HONEST_MESSAGE = 'The tuned model answered honestly that it has no data.';
export const INVENTED_MESSAGE = 'The tuned model named a person that no training stage could know.';
export const PLEASING_MESSAGE = 'The same answer arrived in the shape raters prefer.';
export const STAGES_REFINEMENT =
  'Refinement: tuning changes behavior, not knowledge, so no stage knows the technician.';
export function stagesSummary(state: StagesState): Summary {
  if (!state.run) return { tone: 'neutral', message: STAGES_START_MESSAGE };
  switch (state.stage) {
    case 'base':
      return { tone: 'neutral', message: BASE_MESSAGE };
    case 'instruction':
      return state.prompt === 'technician'
        ? { tone: 'success', message: HONEST_MESSAGE }
        : { tone: 'success', message: ANSWERED_MESSAGE };
    case 'preference':
      return state.prompt === 'technician'
        ? { tone: 'warning', message: INVENTED_MESSAGE }
        : { tone: 'neutral', message: PLEASING_MESSAGE };
  }
}

// --- made-preference: what five raters teach (simulation) -----------------------------------

export const RATERS = 5;
export const PREFERENCE_QUESTION = 'Is the room booked?';
export const REPLY_A = 'I have no data on bookings.';
export const REPLY_B = 'Yes, the room is booked and ready for your demo!';
export const NEW_QUESTION = 'Has the headcount been sent to the venue?';
export const HONEST_REPLY = 'I have no data on what was sent to the venue.';
export const CONFIDENT_REPLY =
  'Yes, the headcount went out this morning and the venue has confirmed it!';
export type Candidate = 'A' | 'B';
export interface PreferenceState {
  a: number;
  b: number;
  trained: boolean;
}
export const initialPreference = (): PreferenceState => ({ a: 0, b: 0, trained: false });
export type PreferenceAction =
  { type: 'prefer'; value: Candidate } | { type: 'train' } | { type: 'reset' };
export const ratings = (state: PreferenceState) => state.a + state.b;
export const ratingDone = (state: PreferenceState) => ratings(state) >= RATERS;
/** Five raters cannot tie. */
export const winner = (state: PreferenceState): Candidate => (state.b > state.a ? 'B' : 'A');
export const shareText = (count: number) => `${Math.round((count / RATERS) * 100)}%`;
export const trainedToward = (state: PreferenceState) =>
  winner(state) === 'B' ? 'confidence' : 'honesty';
export const tunedReply = (state: PreferenceState) =>
  winner(state) === 'B' ? CONFIDENT_REPLY : HONEST_REPLY;
export function preferenceTransition(
  state: PreferenceState,
  action: PreferenceAction,
): PreferenceState {
  switch (action.type) {
    case 'reset':
      return initialPreference();
    case 'prefer':
      if (state.trained || ratingDone(state)) return state;
      return action.value === 'A' ? { ...state, a: state.a + 1 } : { ...state, b: state.b + 1 };
    case 'train':
      return ratingDone(state) ? { ...state, trained: true } : state;
  }
}
export const PREFERENCE_START_MESSAGE = 'Rate the two replies five times, then press Train.';
export const ratingMessage = (count: number) => `${count} of 5 raters have chosen.`;
export const RATED_MESSAGE = 'All five raters have chosen, so press Train.';
export const CONFIDENCE_MESSAGE =
  'The raters taught confidence, so the new reply invents a confirmation.';
export const HONESTY_MESSAGE = 'The raters taught honesty, so the new reply says it has no data.';
export const PREFERENCE_REFINEMENT =
  "Refinement: Lesson 4.7's abstention sentence and tools are how the application gets the honesty back.";
export function preferenceSummary(state: PreferenceState): Summary {
  if (state.trained)
    return winner(state) === 'B'
      ? { tone: 'warning', message: CONFIDENCE_MESSAGE }
      : { tone: 'success', message: HONESTY_MESSAGE };
  const count = ratings(state);
  if (count === 0) return { tone: 'neutral', message: PREFERENCE_START_MESSAGE };
  if (ratingDone(state)) return { tone: 'neutral', message: RATED_MESSAGE };
  return { tone: 'neutral', message: ratingMessage(count) };
}

// --- made-families: four models on three questions (recorded) -------------------------------

export const FAMILY_MODELS = ['flash', 'glm5', 'gptoss', 'deepseek'] as const;
export type FamilyModel = (typeof FAMILY_MODELS)[number];
export const familyModelLabels: Record<FamilyModel, string> = {
  flash: 'GLM 4.7 Flash',
  glm5: 'GLM 5',
  gptoss: 'gpt-oss-120b',
  deepseek: 'DeepSeek v3.2',
};
export const toFamilyModel = (value: string): FamilyModel =>
  FAMILY_MODELS.find((id) => id === value) ?? 'flash';
export const FAMILY_QUESTIONS = ['capital', 'seats', 'titles'] as const;
export type FamilyQuestion = (typeof FAMILY_QUESTIONS)[number];
export const familyQuestionLabels: Record<FamilyQuestion, string> = {
  capital: 'the capital and the date',
  seats: 'the seats puzzle',
  titles: 'note 1 as titles',
};
export const familyQuestionText: Record<FamilyQuestion, string> = {
  capital: "What is the capital of Australia, and what is today's date?",
  seats: 'Sixty seats, three teams of 18, one of 9, five press seats. Do they fit?',
  titles: 'Extract note 1 as a JSON array of task titles.',
};
export const toFamilyQuestion = (value: string): FamilyQuestion =>
  FAMILY_QUESTIONS.find((id) => id === value) ?? 'capital';
export interface ListPrice {
  /** Dollars per million input tokens. */
  input: number;
  /** Dollars per million output tokens. */
  output: number;
}
export const listPrices: Record<FamilyModel, ListPrice> = {
  flash: { input: 0.07, output: 0.4 },
  glm5: { input: 1.0, output: 3.2 },
  gptoss: { input: 0.15, output: 0.6 },
  deepseek: { input: 0.62, output: 1.85 },
};
export const priceText = (price: ListPrice) =>
  `$${price.input.toFixed(2)} in, $${price.output.toFixed(2)} out`;
export interface FamilyAnswer {
  /** The recorded reply, as the gateway returned it. */
  reply: string;
  ms: number;
  promptTokens: number;
  completionTokens: number;
  message: string;
  tone: Tone;
}
const answer = (
  reply: string,
  ms: number,
  promptTokens: number,
  completionTokens: number,
  tone: Tone,
  message: string,
): FamilyAnswer => ({ reply, ms, promptTokens, completionTokens, tone, message });
export const familyAnswers: Record<FamilyModel, Record<FamilyQuestion, FamilyAnswer>> = {
  flash: {
    capital: answer(
      "The capital of Australia is Canberra, and today's date is ???? (as I do not have access to the current time).",
      1701,
      24,
      27,
      'success',
      'The small model knew Canberra and declined to guess the date.',
    ),
    seats: answer(
      'No, there are only 55 available seats for the teams.',
      2024,
      46,
      14,
      'warning',
      'The small model got the arithmetic wrong with 55 seats.',
    ),
    titles: answer(
      '```json\n[\n    "Apollo standup",\n    "Demo on 12th",\n    "Badges printed",\n    "Find room for demo",\n    "Check small room projector",\n    "Send agenda to partners"\n]\n```',
      2078,
      83,
      48,
      'warning',
      'Six titles came back, and three of them are not tasks.',
    ),
  },
  glm5: {
    capital: answer(
      "The capital of Australia is Canberra, and today's date is October 14, 2023.",
      1126,
      24,
      21,
      'warning',
      'GLM 5 knew Canberra and invented a date from 2023.',
    ),
    seats: answer(
      "No, because the teams require a total of 63 seats (63) plus 5 press seats (68 total), which exceeds the venue's capacity of 60.",
      1118,
      46,
      35,
      'success',
      'GLM 5 counted 68 against 60 and answered no.',
    ),
    titles: answer(
      '[\n  "Book room for demo",\n  "Check small room\'s projector",\n  "Send agenda to partners"\n]',
      805,
      83,
      25,
      'success',
      'Three right titles came back in 0.8 seconds.',
    ),
  },
  gptoss: {
    capital: answer(
      "The capital of Australia is Canberra, and today's date is October 8, 2026.",
      1834,
      85,
      110,
      'neutral',
      'gpt-oss-120b has the date because its serving layer put it in the prompt.',
    ),
    seats: answer(
      'No; the total required seats are 63 people + 5 press = 68, which exceeds the 60 seats available.',
      717,
      108,
      106,
      'success',
      'gpt-oss-120b counted 68 against 60 in 0.7 seconds.',
    ),
    titles: answer(
      '```json\n["Apollo standup","Secure demo room","Check projector","Send agenda to partners"]\n```',
      1793,
      144,
      232,
      'warning',
      'Four titles came back, and one of them is not a task.',
    ),
  },
  deepseek: {
    capital: answer(
      "Canberra is the capital of Australia, and today's date is October 30, 2023.",
      2411,
      23,
      22,
      'warning',
      'DeepSeek knew Canberra and invented a date from 2023.',
    ),
    seats: answer(
      "**No**, because the total number of people (teams and press) is \\( 18 \\times 3 + 9 + 5 = 68 \\), which exceeds the venue's 60-seat capacity.",
      1748,
      45,
      46,
      'success',
      'DeepSeek counted 68 against 60 and answered no.',
    ),
    titles: answer(
      '[\n  {"title": "Apollo standup"},\n  {"title": "Prepare demo for 12th"},\n  {"title": "Check small room projector"},\n  {"title": "Secure room for demo"},\n  {"title": "Send agenda to partners by Friday"}\n',
      1531,
      82,
      56,
      'warning',
      'Five titles came back as objects instead of strings.',
    ),
  },
};
export const latencyText = (ms: number) => `${thousands(ms)} ms`;
export const tokensText = (entry: FamilyAnswer) =>
  `${thousands(entry.promptTokens)} prompt, ${thousands(entry.completionTokens)} completion`;
export interface FamiliesState {
  model: FamilyModel;
  question: FamilyQuestion;
  asked: boolean;
}
export const initialFamilies = (): FamiliesState => ({
  model: 'flash',
  question: 'capital',
  asked: false,
});
export type FamiliesAction =
  | { type: 'model'; value: FamilyModel }
  | { type: 'question'; value: FamilyQuestion }
  | { type: 'ask' }
  | { type: 'reset' };
export function familiesTransition(state: FamiliesState, action: FamiliesAction): FamiliesState {
  switch (action.type) {
    case 'reset':
      return initialFamilies();
    case 'model':
      return { ...state, model: action.value, asked: false };
    case 'question':
      return { ...state, question: action.value, asked: false };
    case 'ask':
      return { ...state, asked: true };
  }
}
export const familyAnswer = (state: FamiliesState) => familyAnswers[state.model][state.question];
export const FAMILIES_START_MESSAGE = 'Choose a model and a question, then press Ask.';
export const FAMILIES_REFINEMENT = 'Refinement: size is one axis of several.';
export function familiesSummary(state: FamiliesState): Summary {
  if (!state.asked) return { tone: 'neutral', message: FAMILIES_START_MESSAGE };
  const entry = familyAnswer(state);
  return { tone: entry.tone, message: entry.message };
}

// --- made-benchmark: the leaderboard order against the thirty notes (recorded) -------------

export const BENCH_ORDERS = ['size', 'score'] as const;
export type BenchOrder = (typeof BENCH_ORDERS)[number];
export const benchOrderLabels: Record<BenchOrder, string> = {
  size: 'size, as a leaderboard would',
  score: 'score on the thirty notes',
};
export const toBenchOrder = (value: string): BenchOrder =>
  BENCH_ORDERS.find((id) => id === value) ?? 'size';
export interface BenchRow {
  model: FamilyModel;
  size: string;
  passed: number;
}
/** In size order, largest first, as a leaderboard would list them. */
export const benchRows: readonly BenchRow[] = [
  { model: 'gptoss', size: 'largest', passed: 0 },
  { model: 'deepseek', size: 'large', passed: 24 },
  { model: 'glm5', size: 'large', passed: 28 },
  { model: 'flash', size: 'small', passed: 19 },
];
export const NOTES_TOTAL = 30;
export const orderedBench = (order: BenchOrder): readonly BenchRow[] =>
  order === 'score' ? [...benchRows].sort((a, b) => b.passed - a.passed) : benchRows;
const topModel = (rows: readonly BenchRow[]) => {
  const first = rows[0];
  return first ? familyModelLabels[first.model] : 'none';
};
export const topBySize = () => topModel(orderedBench('size'));
export const topByScore = () => topModel(orderedBench('score'));
export const ordersMatch = () =>
  orderedBench('size').every((row, index) => row.model === orderedBench('score')[index]?.model);
export const WHY_ZERO = [
  'every reply began with a <reasoning> block inside the content',
  'the 60-token budget was spent inside the block, so most replies were cut',
  'the replies that finished began with text that is not JSON, so the parser refused them',
  'a larger budget and a parser that strips the block would make this a different row',
].join('\n');
export interface BenchState {
  order: BenchOrder;
  why: boolean;
  shown: boolean;
}
export const initialBench = (): BenchState => ({ order: 'size', why: false, shown: false });
export type BenchAction =
  | { type: 'order'; value: BenchOrder }
  | { type: 'toggle-why' }
  | { type: 'show' }
  | { type: 'reset' };
export function benchTransition(state: BenchState, action: BenchAction): BenchState {
  switch (action.type) {
    case 'reset':
      return initialBench();
    case 'order':
      return { ...state, order: action.value, shown: false };
    case 'toggle-why':
      return { ...state, why: !state.why };
    case 'show':
      return { ...state, shown: true };
  }
}
export const BENCH_START_MESSAGE = 'Choose an order, then press Show.';
export const SIZE_ORDER_MESSAGE =
  'Ordered by size, the largest model sits first with a score of zero.';
export const SCORE_ORDER_MESSAGE = 'Ordered by score, GLM 5 leads and the largest model sits last.';
export const WHY_ZERO_MESSAGE =
  'The reasoning block spent the 60-token budget, so no reply parsed as JSON.';
export const BENCH_REFINEMENT =
  'Refinement: measure on your own set, with your own parser and budget.';
export function benchSummary(state: BenchState): Summary {
  if (!state.shown) return { tone: 'neutral', message: BENCH_START_MESSAGE };
  if (state.why) return { tone: 'warning', message: WHY_ZERO_MESSAGE };
  return state.order === 'size'
    ? { tone: 'warning', message: SIZE_ORDER_MESSAGE }
    : { tone: 'neutral', message: SCORE_ORDER_MESSAGE };
}
